import React, { useState } from 'react';
import { X, Package, User, Calendar, MapPin, FileText, AlertTriangle } from 'lucide-react';
import InspectionModal from './InspectionModal';

const EquipmentDetailsModal = ({ isOpen, onClose, equipmentDetails }) => {
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [selectedEquipmentForInspection, setSelectedEquipmentForInspection] = useState(null);
  
  if (!isOpen || !equipmentDetails) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInspectionStatus = (equipment) => {
    const latestInspection = equipment.latest_inspection;
    if (!latestInspection || !latestInspection.validity_date) {
      return { color: 'gray', text: 'No inspection data' };
    }
    
    const validityDate = new Date(latestInspection.validity_date);
    const today = new Date();
    const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));
    
    if (latestInspection.status === 'non-ok') {
      return { color: 'red', text: 'Failed inspection' };
    } else if (daysUntilExpiry < 0) {
      return { color: 'red', text: `Expired ${Math.abs(daysUntilExpiry)} days ago` };
    } else if (daysUntilExpiry <= 30) {
      return { color: 'orange', text: `Expires in ${daysUntilExpiry} days` };
    } else if (daysUntilExpiry <= 60) {
      return { color: 'yellow', text: `Expires in ${daysUntilExpiry} days` };
    } else {
      return { color: 'green', text: `Valid for ${daysUntilExpiry} days` };
    }
  };

  const handleOpenInspections = (equipment) => {
    setSelectedEquipmentForInspection(equipment);
    setShowInspectionModal(true);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Equipment Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {equipmentDetails.length === 1 ? (
          // Single equipment item
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">User Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <User className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="font-medium">Name:</span>
                      <span className="ml-2">{equipmentDetails[0].user.first_name} {equipmentDetails[0].user.last_name}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="ml-6 font-medium">Username:</span>
                      <span className="ml-2">{equipmentDetails[0].user.username}</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Equipment Type</h4>
                  <div className="flex items-center text-sm">
                    <Package className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{equipmentDetails[0].equipmentType.name}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-semibold text-gray-700 mb-3">Equipment Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Equipment ID:</span>
                    <p className="text-sm">{equipmentDetails[0].equipment.id}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Serial Number:</span>
                    <p className="text-sm">{equipmentDetails[0].equipment.safety_equipment_serial_number || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Brand:</span>
                    <p className="text-sm">{equipmentDetails[0].equipment.safety_equipment_brand || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Model:</span>
                    <p className="text-sm">{equipmentDetails[0].equipment.safety_equipment_model || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Size:</span>
                    <p className="text-sm">{equipmentDetails[0].equipment.safety_equipment_size || '-'}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <p className="text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        equipmentDetails[0].equipment.safety_equipment_status === 'assigned' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {equipmentDetails[0].equipment.safety_equipment_status}
                      </span>
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">VI Number:</span>
                    <p className="text-sm">{equipmentDetails[0].equipment.safety_equipment_vi_number || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Manufacture Date:</span>
                    <p className="text-sm">{formatDate(equipmentDetails[0].equipment.safety_equipment_manufacture_date)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Assignment Date:</span>
                    <p className="text-sm">{formatDate(equipmentDetails[0].assignment.assignment_date)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Location:</span>
                    <p className="text-sm">{equipmentDetails[0].assignment.assignment_location || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-gray-700">Inspection Information</h4>
                <button
                  onClick={() => handleOpenInspections(equipmentDetails[0].equipment)}
                  className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  View All Inspections
                </button>
              </div>
              <div className="space-y-3">
                {equipmentDetails[0].equipment.latest_inspection ? (
                  <>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Latest Inspection Status:</span>
                      <div className="mt-1">
                        {(() => {
                          const status = getInspectionStatus(equipmentDetails[0].equipment);
                          return (
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                                status.color === 'red' ? 'bg-red-100 text-red-800' :
                                status.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                                status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                status.color === 'green' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {formatDate(equipmentDetails[0].equipment.latest_inspection.validity_date)}
                              </span>
                              <span className="text-sm text-gray-600">{status.text}</span>
                              {status.color === 'red' && <AlertTriangle className="h-4 w-4 text-red-600" />}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-sm font-medium text-gray-500">Last Inspection Date:</span>
                      <p className="text-sm mt-1">{formatDate(equipmentDetails[0].equipment.latest_inspection.inspection_date)}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm font-medium text-gray-500">Inspector:</span>
                      <p className="text-sm mt-1">{equipmentDetails[0].equipment.latest_inspection.inspector || '-'}</p>
                    </div>

                    <div>
                      <span className="text-sm font-medium text-gray-500">Inspection Result:</span>
                      <p className="text-sm mt-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          equipmentDetails[0].equipment.latest_inspection.status === 'ok' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {equipmentDetails[0].equipment.latest_inspection.status === 'ok' ? 'OK' : 'Non-OK'}
                        </span>
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p>No inspection records found.</p>
                    <button
                      onClick={() => handleOpenInspections(equipmentDetails[0].equipment)}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Add first inspection
                    </button>
                  </div>
                )}
              </div>
            </div>

            {equipmentDetails[0].equipment.safety_equipment_comment && (
              <div className="bg-white border rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  Comments
                </h4>
                <p className="text-sm text-gray-600">{equipmentDetails[0].equipment.safety_equipment_comment}</p>
              </div>
            )}
          </div>
        ) : (
          // Multiple equipment items
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">
                User <span className="font-semibold">{equipmentDetails[0].user.first_name} {equipmentDetails[0].user.last_name}</span> has 
                <span className="font-semibold"> {equipmentDetails.length} </span> 
                items of type <span className="font-semibold">{equipmentDetails[0].equipmentType.name}</span>
              </p>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {equipmentDetails.map((detail, index) => {
                const status = getInspectionStatus(detail.equipment);
                return (
                  <div key={index} className="bg-white border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium text-gray-900">Equipment #{detail.equipment.id}</h5>
                      <button
                        onClick={() => handleOpenInspections(detail.equipment)}
                        className="flex items-center px-2 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        Inspections
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <span className="text-xs font-medium text-gray-500">Equipment ID</span>
                        <p className="text-sm font-semibold">{detail.equipment.id}</p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Serial Number</span>
                        <p className="text-sm">{detail.equipment.safety_equipment_serial_number || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Validity Date</span>
                        <div className="mt-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            status.color === 'red' ? 'bg-red-100 text-red-800' :
                            status.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                            status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            status.color === 'green' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {detail.equipment.latest_inspection?.validity_date ? 
                              formatDate(detail.equipment.latest_inspection.validity_date) : 
                              'No inspection'
                            }
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Location</span>
                        <p className="text-sm">{detail.assignment.assignment_location || '-'}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Close
          </button>
        </div>
      </div>

      {/* Inspection Modal */}
      <InspectionModal
        isOpen={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        equipmentId={selectedEquipmentForInspection?.id}
        equipmentName={selectedEquipmentForInspection ? (
          selectedEquipmentForInspection.safety_equipment_model_name || 
          selectedEquipmentForInspection.safety_equipment_type_name ||
          `Equipment #${selectedEquipmentForInspection.id}`
        ) : ''}
        equipmentSerialNumber={selectedEquipmentForInspection?.safety_equipment_serial_number}
        onSave={() => {
          // Could refresh data here if needed
        }}
      />
    </div>
  );
};

export default EquipmentDetailsModal;